import React from 'react'

import { Button, hasValue, Tooltip, type TooltipProps } from '../../module'
import FormLabel from '../FormLabel/FormLabel'
import styles from './_picker.module.scss'
import { type ButtonProps } from '../Button/Button.models'

export default function Picker<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
  StateGeneric,
>({
  options,
  state,
  callout,
  stateName,
  customClass,
  valuesAreBooleans,
  disabled,
}: StateAndStateNameProps<OptionGeneric, StateGeneric>): React.JSX.Element

export default function Picker<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
  StateGeneric,
>({
  options,
  selected,
  callout,
  customClass,
  valuesAreBooleans,
  disabled,
}: SelectedProps<OptionGeneric, StateGeneric>): React.JSX.Element

export default function Picker<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
  StateGeneric,
>({
  options,
  selected,
  state,
  callout,
  stateName,
  customClass,
  valuesAreBooleans = false,
  disabled = false,
  error = false,
  required,
  labelText,
  rightLabel,
  labelTooltip,
  tooltip,
  qaTestId = 'picker',
}: PickerProps<OptionGeneric, StateGeneric>): React.JSX.Element {
  const inlineStyles = {
    '--options-length': options.length,
  } as React.CSSProperties

  const renderPicker = () => {
    return (
      <div
        data-id='picker-container'
        className={`${styles.container} ${customClass || ''} ${
          disabled ? styles.disabled : ''
        } ${error ? styles.errorBorder : ''}`}
        style={inlineStyles}
      >
        {options.map((e, i) => {
          return (
            <Button
              as='unstyled'
              data-id='picker-button'
              key={e.text ?? e.id ?? i}
              className={`${styles.button} ${
                valuesAreBooleans
                  ? e.value === selected
                    ? styles.active
                    : ''
                  : e.value ===
                      (selected ||
                        (state as Record<string | number, unknown>)?.[
                          stateName ?? ''
                        ])
                    ? styles.active
                    : ''
              } ${error ? styles.errorBorder : ''}`}
              onClick={() => {
                if (!disabled) {
                  if (hasValue(selected)) {
                    const actualCallout = callout as SelectedPropsCallout<
                      OptionGeneric['value']
                    >
                    actualCallout(e.value)
                  } else {
                    const actualCallout = callout as StateAndStateNameCallout<
                      OptionGeneric['value']
                    >
                    actualCallout(stateName ?? '', e.value)
                  }
                }
              }}
              data-text={e.text}
            >
              <span className={styles.picker}>{e.text}</span>
            </Button>
          )
        })}
      </div>
    )
  }
  return (
    <div qa-test-id={qaTestId}>
      {labelText && (
        <FormLabel
          label={labelText}
          tooltip={labelTooltip}
          required={required}
          rightLabel={rightLabel}
          disabled={disabled}
          error={error}
        />
      )}
      {tooltip ? (
        <Tooltip position='bottom' {...tooltip}>
          {renderPicker()}
        </Tooltip>
      ) : (
        renderPicker()
      )}
    </div>
  )
}

/**
 *  pass in an array of options to select that will be mapped out and styled for you
 *  - then pass in what value is currently selected in state or pass in a state object
 *  w/ fieldName and the component will show that option as selected - state needs to
 *  be managed outside of this component and in the file you are consuming it in.
 **/
interface RequiredOptionKeys<ValueTypeGeneric> {
  id?: number
  text?: string
  value: ValueTypeGeneric
}

type Selected = string | number | boolean

type PickerBaseProps<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
> = {
  options: OptionGeneric[]
  customClass?: string
  valuesAreBooleans?: boolean
  disabled?: boolean
  labelText?: string
  rightLabel?: React.ReactNode
  labelTooltip?: Omit<TooltipProps, 'children'>
  required?: boolean
  error?: boolean
  /** Optional prop to add a test id to the Picker for QA testing */
  qaTestId?: string
}

type StateAndStateNameCallout<Param2Generic> = (
  param1: string,
  param2: Param2Generic,
) => void

// if you use state, then it and stateName are both required.
type StateAndStateNameProps<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
  StateGeneric,
> = PickerBaseProps<OptionGeneric> & {
  state: StateGeneric
  stateName: string
  selected?: Selected
  callout: StateAndStateNameCallout<OptionGeneric['value']>
  tooltip?: ButtonProps['tooltip']
}

type SelectedPropsCallout<Param1Generic> = (param1: Param1Generic) => void
// if you use selected, then it's required... unless there isn't anything selected yet. Then it's optional
type SelectedProps<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
  StateGeneric,
> = PickerBaseProps<OptionGeneric> & {
  state?: StateGeneric
  stateName?: string
  selected?: Selected
  callout: SelectedPropsCallout<OptionGeneric['value']>
  tooltip?: ButtonProps['tooltip']
}

export type PickerProps<
  OptionGeneric extends RequiredOptionKeys<OptionGeneric['value']>,
  StateGeneric,
> =
  | SelectedProps<OptionGeneric, StateGeneric>
  | StateAndStateNameProps<OptionGeneric, StateGeneric>

// here's an example of passed in options
// [
//   { id: 0, text: 'SNOWBOARDING', value: 'SNOWBOARDING' },
//   { id: 1, text: 'SKIING', value: 'SKIING' },
// ]

// selected value could be - 'SNOWBOARDING'
// or if state obj and stateName i.e. key value pair - {preferred_method: 'SNOWBOARDING'} and would // statifsy component
